import React, {FC, ReactNode, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import classNames from "classnames";
import {observer} from "mobx-react-lite";
import {nanoid} from "nanoid";
import {useAppState} from "@stores/AppStateContext";

import {IconProps, IconSize} from "../../icons";

import {ExpandMoreIcon} from "../../icons/ExpandMoreIcon";
import {CloseIcon} from "../../icons/CloseIcon";
import {openSelectOptionsCtxMenu} from "./openSelectOptionsCtxMenu";

import './Select.css';

export interface SelectOption {
    value: string;
    plainLabel?: string;
    label: ReactNode;
    textForFilter?: string;
    description?: string;
    condition?: boolean;
    Icon?: React.ComponentType<IconProps>;
    activeIcon?: React.ComponentType<IconProps>;
    moreAction?: (event: React.MouseEvent<HTMLDivElement>) => void;
    withBorder?: boolean;
}

export interface SelectPropsBase {
    options: SelectOption[];
    renderSelectedOption?: (selectedOption: SelectOption) => ReactNode;
    className?: string;
    id?: string;
    isFullWidth?: boolean;
    allowFilter?: boolean;
    allowEmpty?: boolean;
    isDark?: boolean;
    maxWidth?: number;
    maxHeight?: number;
    hasIconBeforeHeader?: boolean;
    iconSize?: IconSize;
    placeholder?: string;
    selectBoxMinWidth?: number;
    Icon?: React.ComponentType<IconProps>;
    size: "small" | "medium" | "large" | "extraLarge";
    fontSize?: string;
    style?: "default" | 'select2' | 'plainFilter';
    hasError?: boolean;
    headerNode?: React.ReactNode;
    withBorder?: boolean;
    withLabel?: boolean;
    shouldForcePlace?: boolean
    ignoreActiveHighlight?: boolean
    valueOnReset?: string;
    isTransparentBg?: boolean;
}

export type SingleSelectProps = SelectPropsBase & {
    allowMulti?: false;
    selectedValue: string | null;
    onChange: (selectedValue: string | null) => void;
}

export type MultiSelectProps = SelectPropsBase & {
    allowMulti: true;
    selectedValues: string[] | null;
    onChange: (selectedValues: string[] | null) => void;
}

export type SelectProps = SingleSelectProps | MultiSelectProps;

export const Select: FC<SelectProps> = observer((
    props
) => {
    const appState = useAppState();
    const {
        className,
        options,
        renderSelectedOption = (selectedOption: SelectOption) => selectedOption.label,
        maxHeight,
        maxWidth,
        isFullWidth = false,
        allowFilter = false,
        allowEmpty = false,
        isDark = false,
        iconSize = 20,
        placeholder,
        selectBoxMinWidth,
        hasIconBeforeHeader = false,
        Icon,
        size,
        fontSize,
        hasError: hasError,
        headerNode,
        withBorder = false,
        allowMulti = false,
        withLabel = false,
        shouldForcePlace = false,
        ignoreActiveHighlight = false,
        isTransparentBg = false,
        valueOnReset, // reset value on clear. For SingleSelect
        style = 'default',
    } = props;
    let openOrUpdateCtxMenu: () => void;
    const id = useMemo(() => props.id || nanoid(), [props.id]);

    const mainRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [filterQuery, setFilterQuery] = useState("");
    const [isFocused, setIsFocused] = useState(false);

    const isOpen = appState.isCtxMenuOpen('select-options', id);
    let _fontSize = fontSize;
    if (!_fontSize) {
        _fontSize = size === 'large' || size === 'extraLarge' ? 'body-large' : 'body-medium';
    }

    if (isMultiSelect(props)) {
        // `props` is a multi-select variant
        openOrUpdateCtxMenu = () => {
            const selectedValues = props.selectedValues;
            openSelectOptionsCtxMenu(
                appState,
                mainRef.current!,
                {
                    selectedValues: selectedValues,
                    allowMulti: true,
                    options: options,
                    onChange: props.onChange,
                    maxHeight,
                    allowFilter,
                    filterQuery,
                    maxWidth,
                    headerNode,
                    withBorder,
                    shouldForcePlace
                },
                {
                    isDark: isDark,
                    onClose: onClose,
                    id: id,
                    VERTICAL_SHIFT: 4
                },
            );
        }
    } else {
        // `props` is a single-select variant
        openOrUpdateCtxMenu = () => {
            openSelectOptionsCtxMenu(
                appState,
                mainRef.current!,
                {
                    selectedValue: props.selectedValue,
                    allowMulti: false,
                    options,
                    onChange: props.onChange,
                    maxHeight,
                    allowFilter,
                    filterQuery,
                    maxWidth,
                    headerNode,
                    withBorder,
                    shouldForcePlace
                },
                {
                    isDark: isDark,
                    onClose: onClose,
                    id: id,
                    VERTICAL_SHIFT: 4
                },
            );
        }
    }

    const onClose = () => {
        setIsFocused(false)
        setFilterQuery("");
    };

    const onClick = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        if (isOpen) {
            appState.closeContextMenusByType('select-options', id)
        } else {
            openOrUpdateCtxMenu();
        }
    }

    const clearFilter = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        if (valueOnReset && (props as SingleSelectProps).selectedValue !== valueOnReset) {
            (props as SingleSelectProps).onChange(valueOnReset);
        } else {
            setFilterQuery("");
            props.onChange(null);
        }
    }

    useLayoutEffect(() => {
        if (allowFilter && isOpen) {
            setFilterQuery("");
            setTimeout(() => {
                inputRef.current?.focus();
            }, 0);
            setIsFocused(true)
        }
    }, [allowFilter, isOpen, inputRef.current, (props as MultiSelectProps).selectedValues]);

    useEffect(() => {
        if (isOpen) {
            openOrUpdateCtxMenu();
        }
    }, [isOpen, filterQuery, (props as MultiSelectProps).selectedValues]);

    const setSelectBoxMinWidth = selectBoxMinWidth ? {minWidth: `${selectBoxMinWidth}px`} : {};
    let isLabelRaised = (
        Array.isArray((props as MultiSelectProps).selectedValues) && (props as MultiSelectProps).selectedValues!.length > 0
    ) || isFocused;
    const renderFilterInput = () => {
        return (
            <>
                {(
                    <>
                        <input
                            ref={inputRef}
                            type="text"
                            value={filterQuery}
                            onChange={(event) => setFilterQuery(event.target.value)}
                            className={`Select__box-input ${_fontSize}`}
                            placeholder={!withLabel && isFocused ? placeholder : ''}
                        />
                    </>
                )}

            </>
        );
    };

    const renderSingleSelect = (props: SingleSelectProps) => {
        const selectedOption = props.selectedValue === null ? null : options.find(option => option.value === props.selectedValue);
        const selectedOptionText = selectedOption ? selectedOption.label : "";

        return (
            <div className="Select__box--Text">
                {
                    allowFilter && isOpen
                        ? renderFilterInput()
                        : (
                            <div
                                className={classNames({
                                    "Select__box--Text-content": true,
                                    "Select__box--Text-content--placeholder": selectedOptionText === ""
                                })}
                            >
                                {
                                    selectedOption
                                        ? <div className="Select_option__text"> {renderSelectedOption(selectedOption)}</div>
                                        : (
                                            <span>{placeholder}</span>
                                        )
                                }
                            </div>
                        )
                }
            </div>
        )
    }
    const renderMultiSelect = (props: MultiSelectProps) => (
        <div className={`Select__box--Text ${_fontSize} MultiSelect`}>
            {
                props.selectedValues === null
                    ? null
                    : props.selectedValues
                        .map((value) => options.find(option => option.value === value))
                        .map(option => (
                            !option
                                ? null
                                : (
                                    <div key={option.value} className="MultiSelect__value label-large">
                                        {
                                            <>
                                                <span className="MultiSelect__value-text">{option.plainLabel}</span>
                                                <CloseIcon
                                                    className="MultiSelect-remove"
                                                    size={16}
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        event.preventDefault();
                                                        props.onChange(
                                                            props.selectedValues === null
                                                                ? null
                                                                : props.selectedValues.filter(
                                                                    checkVal => checkVal !== option.value
                                                                )
                                                        );
                                                    }}
                                                />
                                            </>

                                        }
                                    </div>
                                )
                        ))
            }
            {isFocused && renderFilterInput()}
            {
                !withLabel && !isFocused && (!props.selectedValues || props.selectedValues?.length === 0)
                    ? <span className={"Select__box--Text-content--placeholder"}>{placeholder}</span>
                    : null
            }
            {withLabel && (
                <label className={classNames({
                    'Select__label': true,
                    [_fontSize]: !isFocused && !isLabelRaised,
                    'label-large Select__label--isRaised': isLabelRaised,
                    'label-large': !isFocused && isLabelRaised,
                    'Select__label--isFocused': isFocused
                })}>
                    {placeholder}
                </label>
            )}
        </div>
    );

    const hasValue: boolean = !!((props as MultiSelectProps).selectedValues || (props as SingleSelectProps).selectedValue);
    const shouldShowActive = (isOpen || (hasValue && !ignoreActiveHighlight));
    return (
        <div className={classNames({
            'Select': true,
            'Select--multi': allowMulti,
            'Select--2': style === 'select2',
            'Select--plainFilter': style === 'plainFilter',
            // Active styles only when menu is open or value is clearable (allowEmpty)
            'Select--active': shouldShowActive,
            'Select--fullwidth': isFullWidth,
            'Select--allowFilter': allowFilter,
            'theme-dark': isDark,
            'SelectInput--error': hasError,
            'Select--transparent': isTransparentBg,
            'SelectInput--focused': isFocused && allowFilter,
            'SelectInput--unfocused': !isFocused && allowFilter,
            'Select-withBorder': withBorder,
            [className!]: !!className,
            [`selectSize--${size}`]: size,
        })}
             ref={mainRef}
             onClick={onClick}
             style={setSelectBoxMinWidth}
        >
            <div className={classNames(_fontSize, {
                "Select__box": true,
                "Select--box__withIcon": Icon || hasIconBeforeHeader
            })}>
                {
                    !hasValue && Icon && <Icon className="Select__box-boxIcon" size={iconSize}/>
                }
                {
                    isMultiSelect(props)
                        ? renderMultiSelect(props)
                        : renderSingleSelect(props)
                }
                {
                    hasValue && (allowEmpty && !allowMulti || (valueOnReset && (props as SingleSelectProps).selectedValue !== valueOnReset))
                        ? <CloseIcon className="Select__box-icon" size={iconSize} onClick={clearFilter}/>
                        : <ExpandMoreIcon className={classNames({
                            "Select__box-icon": true,
                            "Select--box-icon--active": isDark && isOpen
                        })} size={iconSize}/>
                }
            </div>
        </div>
    );
});

export function isMultiSelect(props: SelectProps): props is MultiSelectProps {
    return props.allowMulti === true;
}

/*
* Select2 expect to always have a value, that's why his --active styles copy plain state background and not the :hover one.
* */
